import type { GoogleAdsAppDto } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, PasswordInput, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const gQuery = queryOptions({ queryKey: ['platform', 'settings', 'google-ads'], queryFn: () => api<GoogleAdsAppDto>('/platform/settings/google-ads') });

/** Console → Configurações → Google Ads: developer token e cliente OAuth da plataforma (vale para todas as clínicas). */
export function GoogleAdsSettingsCard() {
  const qc = useQueryClient();
  const { data } = useQuery(gQuery);
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [dev, setDev] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (data?.clientId) setClientId(data.clientId); }, [data?.clientId]);
  const save = useMutation({
    mutationFn: () => api<GoogleAdsAppDto>('/platform/settings/google-ads', { method: 'PUT', body: { clientId, ...(secret.trim() ? { clientSecret: secret.trim() } : {}), ...(dev.trim() ? { developerToken: dev.trim() } : {}) } }),
    onSuccess: (r) => { qc.setQueryData(gQuery.queryKey, r); setSecret(''); setDev(''); },
  });
  if (!data) return <Card className="mt-4 grid h-32 place-items-center"><Spinner className="size-5 text-fg-subtle" label="Carregando" /></Card>;
  return (
    <Card className="mt-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-fg"><BarChart3 className="size-4 text-fg-subtle" />Google Ads (conversões offline)</h2>
          <p className="mt-1 text-[13px] text-fg-muted">
            Developer token da conta gerente (MCC) no Google Ads › Central de API, e um cliente OAuth “Aplicativo da Web” no Google Cloud com a API do Google Ads ativada
            e a URL de redirecionamento abaixo. Depois, cada clínica conecta a própria conta em Configurações › Conversões.
          </p>
        </div>
        {data.configured ? <Badge tone="success" className="shrink-0">Configurado</Badge> : <Badge tone="warning" className="shrink-0 whitespace-nowrap">Não configurado</Badge>}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-lg bg-surface-muted p-3.5 text-[13px]">
        <span className="text-fg-muted">URL de redirecionamento</span>
        <span className="min-w-0 flex-1 font-mono break-all text-fg">{data.redirectUri}</span>
        <button type="button" aria-label="Copiar URL" onClick={() => { void navigator.clipboard.writeText(data.redirectUri).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
          className="grid size-7 place-items-center rounded-md text-fg-subtle hover:bg-surface hover:text-fg">{copied ? '✓' : <Copy className="size-3.5" />}</button>
      </div>
      <form className="mt-4 grid gap-4 sm:grid-cols-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="ID do cliente OAuth"><Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="…apps.googleusercontent.com" /></Field>
        <Field label="Chave secreta do cliente" hint={data.clientSecretSet ? 'Já salva. Preencha só para trocar.' : undefined}><PasswordInput value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off" /></Field>
        <Field label="Developer token" hint={data.developerTokenSet ? 'Já salvo. Preencha só para trocar.' : undefined}><PasswordInput value={dev} onChange={(e) => setDev(e.target.value)} autoComplete="off" /></Field>
        {save.error && <div className="sm:col-span-3"><Alert tone="danger">{save.error.message}</Alert></div>}
        <div><Button type="submit" disabled={!clientId.trim()} loading={save.isPending}>Salvar</Button></div>
      </form>
    </Card>
  );
}
